import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { debounceTime, merge, of } from "rxjs";
import { scaleLinear } from "d3";
import AxisHorizontal from "./AxisHorizontal";
import eventEmitter from "../../eventEmitter";
import AxisVertical from "./AxisVertical";
import Bar from "./Bar";

function BarChart({ items, title, getLabel, minHeight, isInModal }) {
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  const [axisOffset, setAxisOffset] = useState(0);
  const containerRef = useRef(null);
  const max = Math.max(...items.map((x) => x.value));

  const yScale = scaleLinear().domain([0, max]).range([height, 0]);
  const xScale = scaleLinear().domain([0, items.length]).range([0, width]);
  const ticks = yScale.nice().ticks(5);
  const axisTicks = ticks.filter((x) => x % 1 === 0);

  const pad = (width / items.length) * 0.2;

  useEffect(() => {
    const sub = merge(of(null), eventEmitter.resizeTracker$)
      .pipe(debounceTime(0))
      .subscribe(() => {
        const bBox = containerRef.current.getBoundingClientRect();
        setHeight(bBox.height);
        setWidth(bBox.width);
      });

    return () => sub.unsubscribe();
  }, [axisOffset, items]);

  const containerStyle = isInModal
    ? {
        minHeight: `${minHeight}px`,
        width: items.length * 50,
        maxWidth: "80vw",
      }
    : {
        minHeight: `${minHeight}px`,
        width: "auto",
      };

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-w-full">
      <div className="tw-text-xs tw-text-center tw-font-semibold tw-opacity-70 tw-mx-4 tw-mt-2">
        {title}
      </div>
      <div className="tw-flex-grow tw-m-2 tw-flex">
        <div className="tw-flex tw-flex-col tw-relative">
          <div style={{ width: axisOffset }} />
          <AxisVertical yScale={yScale} ticks={axisTicks} />
        </div>

        <div className="tw-flex-grow tw-flex tw-flex-col">
          <div
            ref={containerRef}
            style={containerStyle}
            className="tw-relative tw-overflow-hidden tw-flex-grow"
          >
            {ticks.map((x) => (
              <div
                className="tw-absolute tw-border-b tw-border-gray-400 tw-border-opacity-30"
                style={{
                  top: yScale(x),
                  right: 0,
                  transform: "translateY(-50%)",
                  height: 1,
                  width: "100%",
                }}
                key={x}
              />
            ))}

            {items.map((x, i) => (
              <Bar
                key={`${i}}`}
                left={xScale(i) + pad}
                top={height - yScale(x.value) + 1}
                width={xScale(i + 1) - xScale(i) - pad}
                height={height}
                color={x.color}
                description={`${x.key}: ${x.title || x.value}`}
              />
            ))}
          </div>

          <AxisHorizontal
            updateLeftOffset={(x) => setAxisOffset(x)}
            getLabel={getLabel}
            items={items}
            width={width}
          />
        </div>
      </div>
    </div>
  );
}

BarChart.propTypes = {
  title: PropTypes.string.isRequired,
  minHeight: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getLabel: PropTypes.func.isRequired,
  isInModal: PropTypes.bool.isRequired,
};

BarChart.defaultProps = {};

export default BarChart;
